import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Copy, CheckCircle, Smartphone, Building2 } from "lucide-react";
import { WHATSAPP_NUMBER, UPI_ID, BANK_ACCOUNT, BANK_NAME, BANK_IFSC, BANK_BRANCH } from "@/data/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GOLD = "#B89047";
const CHARCOAL = "#1A1A1A";
const OFF_WHITE = "#FDFCF8";
const BORDER = "#E5E0D8";

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${BORDER}` }}>
      <div>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "#8A8A8A", marginBottom: "3px" }}>{label}</p>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.95rem", color: CHARCOAL, fontWeight: 600 }}>{value}</p>
      </div>
      <button
        onClick={copy}
        style={{ background: "none", border: `1px solid ${BORDER}`, padding: "6px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "5px", fontFamily: "'Outfit', sans-serif", fontSize: "0.68rem", color: copied ? GOLD : "#8A8A8A", transition: "all 0.2s" }}
      >
        {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export default function PaymentModal({ service, open, onClose }) {
  const [tab, setTab] = useState("upi");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleClose = () => {
    setTab("upi");
    setName("");
    setPhone("");
    setSubmitted(false);
    onClose();
  };

  const whatsappMsg = service
    ? encodeURIComponent(
        `Hello Akkshay ji, I have made the payment for the following consultation:\n\nService: ${service.name}\nAmount Paid: ₹${service.price.toLocaleString("en-IN")}\nName: ${name || "[Your Name]"}\nPhone: ${phone || "[Your Phone]"}\n\nPlease find the payment screenshot attached.\nPreferred Date: \nConcern: `
      )
    : "";
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMsg}`;

  const handleSendScreenshot = async () => {
    if (name.trim() && phone.trim() && service) {
      try {
        await axios.post(`${API}/booking/intent`, {
          service_name: service.name,
          amount: service.price,
          client_name: name,
          client_phone: phone,
        });
      } catch (e) {}
    }
    setSubmitted(true);
    window.open(whatsappLink, "_blank");
  };

  if (!service) return null;

  const inputStyle = {
    width: "100%", padding: "11px 14px", border: `1px solid ${BORDER}`,
    backgroundColor: OFF_WHITE, fontFamily: "'Outfit', sans-serif",
    fontSize: "0.88rem", color: CHARCOAL, outline: "none",
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent style={{ backgroundColor: OFF_WHITE, border: `1px solid ${BORDER}`, borderRadius: 0, maxWidth: "500px", padding: 0 }}>
        {/* Header */}
        <div style={{ backgroundColor: CHARCOAL, padding: "24px 28px" }}>
          <DialogHeader>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.6rem", letterSpacing: "0.2em", textTransform: "uppercase", color: GOLD, marginBottom: "6px" }}>
              Payment Details
            </p>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.5rem", fontWeight: 500, color: OFF_WHITE, lineHeight: 1.2 }}>
              {service.name}
            </DialogTitle>
            <DialogDescription style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.6rem", fontWeight: 700, color: GOLD, marginTop: "4px" }}>
              ₹{service.price.toLocaleString("en-IN")}{service.priceSuffix || ""}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div style={{ padding: "24px 28px" }}>
          {/* Tab switcher */}
          <div style={{ display: "flex", gap: "0", marginBottom: "20px", border: `1px solid ${BORDER}` }}>
            {[
              { key: "upi", icon: <Smartphone size={13} />, label: "UPI Payment" },
              { key: "bank", icon: <Building2 size={13} />, label: "Bank Transfer" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`payment-tab-${t.key}`}
                style={{
                  flex: 1, padding: "11px", display: "flex", alignItems: "center", justifyContent: "center", gap: "7px",
                  fontFamily: "'Outfit', sans-serif", fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase",
                  fontWeight: 600, border: "none", cursor: "pointer",
                  backgroundColor: tab === t.key ? CHARCOAL : "transparent",
                  color: tab === t.key ? OFF_WHITE : "#8A8A8A",
                  transition: "all 0.2s",
                }}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* UPI Tab */}
          {tab === "upi" && (
            <div data-testid="upi-payment-details">
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.82rem", color: "#4A4A4A", marginBottom: "16px", lineHeight: 1.6 }}>
                Open any UPI app (Google Pay, PhonePe, Paytm, etc.) and pay to the number below.
              </p>
              <div style={{ border: `2px solid ${GOLD}`, padding: "20px 20px 8px", backgroundColor: "#FFFDF5", marginBottom: "16px" }}>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.62rem", letterSpacing: "0.15em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "8px" }}>
                  Pay to Mobile / UPI
                </p>
                <CopyField label="UPI / Mobile Number" value={UPI_ID} />
                <CopyField label="Name" value={BANK_NAME} />
                <div style={{ paddingTop: "12px" }}>
                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.78rem", color: "#8A8A8A", lineHeight: 1.6 }}>
                    Amount: <strong style={{ color: CHARCOAL }}>₹{service.price.toLocaleString("en-IN")}</strong> — Enter manually in your UPI app
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Bank Transfer Tab */}
          {tab === "bank" && (
            <div data-testid="bank-payment-details">
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.82rem", color: "#4A4A4A", marginBottom: "16px", lineHeight: 1.6 }}>
                Transfer the exact amount to the bank account below and send the screenshot on WhatsApp.
              </p>
              <div style={{ border: `2px solid ${GOLD}`, padding: "20px 20px 8px", backgroundColor: "#FFFDF5", marginBottom: "16px" }}>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.62rem", letterSpacing: "0.15em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "8px" }}>
                  Bank Account Details
                </p>
                <CopyField label="Account Number" value={BANK_ACCOUNT} />
                <CopyField label="Account Name" value={BANK_NAME} />
                <CopyField label="IFSC Code" value={BANK_IFSC} />
                <CopyField label="Bank" value={BANK_BRANCH} />
                <div style={{ paddingTop: "12px" }}>
                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.78rem", color: "#8A8A8A", lineHeight: 1.6 }}>
                    Amount: <strong style={{ color: CHARCOAL }}>₹{service.price.toLocaleString("en-IN")}</strong>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Client Info */}
          <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "18px" }}>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your Name"
              data-testid="payment-name-input"
              style={inputStyle}
              onFocus={(e) => { e.target.style.borderColor = GOLD; }}
              onBlur={(e) => { e.target.style.borderColor = BORDER; }}
            />
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Your Phone Number"
              data-testid="payment-phone-input"
              style={inputStyle}
              onFocus={(e) => { e.target.style.borderColor = GOLD; }}
              onBlur={(e) => { e.target.style.borderColor = BORDER; }}
            />
          </div>

          {/* Main CTA */}
          <button
            onClick={handleSendScreenshot}
            data-testid="send-screenshot-whatsapp-btn"
            style={{
              width: "100%", backgroundColor: "#25D366", color: "#fff",
              padding: "14px", fontFamily: "'Outfit', sans-serif",
              fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase",
              fontWeight: 700, border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#1DA851"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#25D366"; }}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            I've Paid — Send Screenshot on WhatsApp
          </button>

          <p style={{ marginTop: "10px", fontSize: "0.72rem", color: "#8A8A8A", fontFamily: "'Outfit', sans-serif", textAlign: "center" }}>
            Booking confirmed only after payment verification by Akkshay Narula.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
